import { useState } from "react";
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from "lucide-react";

const SCORE_COLORS = { 1: "#ef4444", 2: "#f97316", 3: "#eab308", 4: "#84cc16", 5: "#22c55e" };
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function MoodCalendar({ checkIns, scoreKey, title }) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });

  const scoreMap = {};
  checkIns.forEach(c => { if (c.date && c[scoreKey] != null) scoreMap[c.date] = c[scoreKey]; });

  const prevMonth = () => setViewDate(v => v.month === 0 ? { year: v.year - 1, month: 11 } : { ...v, month: v.month - 1 });
  const nextMonth = () => setViewDate(v => { const now = new Date(); if (v.year === now.getFullYear() && v.month === now.getMonth()) return v; return v.month === 11 ? { year: v.year + 1, month: 0 } : { ...v, month: v.month + 1 }; });

  const monthLabel = new Date(viewDate.year, viewDate.month, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
  const firstDay = new Date(viewDate.year, viewDate.month, 1).getDay();
  const daysInMonth = new Date(viewDate.year, viewDate.month + 1, 0).getDate();
  const cells = []; for (let i = 0; i < firstDay; i++) cells.push(null); for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const now = new Date(); const isCurrentMonth = viewDate.year === now.getFullYear() && viewDate.month === now.getMonth();

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid={`calendar-${scoreKey}`}>
      <button onClick={() => setIsOpen(o => !o)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-accent/50 transition-colors">
        <h3 className="text-sm font-semibold">{title}</h3>
        {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>
      {isOpen && (
        <div className="px-4 pb-4 animate-fade-in">
          <div className="flex items-center justify-between mb-3">
            <button onClick={prevMonth} className="p-1 rounded hover:bg-accent"><ChevronLeft className="w-4 h-4 text-muted-foreground" /></button>
            <span className="text-sm font-medium">{monthLabel}</span>
            <button onClick={nextMonth} disabled={isCurrentMonth} className="p-1 rounded hover:bg-accent disabled:opacity-30"><ChevronRight className="w-4 h-4 text-muted-foreground" /></button>
          </div>
          <div className="grid grid-cols-7 mb-1">{DAY_LABELS.map(d => <div key={d} className="text-center text-xs text-muted-foreground font-medium py-1">{d}</div>)}</div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((day, i) => {
              if (!day) return <div key={`e-${i}`} />;
              const dateStr = `${viewDate.year}-${String(viewDate.month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const score = scoreMap[dateStr]; const color = score ? SCORE_COLORS[Math.round(Math.min(5, Math.max(1, score)))] : null;
              const isToday = isCurrentMonth && day === now.getDate();
              return (
                <div key={dateStr} title={score ? `Score: ${score}/5` : "No data"}
                  className="aspect-square flex items-center justify-center rounded-md text-xs font-medium"
                  style={{ backgroundColor: color ? color + "cc" : undefined, color: color ? "#fff" : undefined,
                    border: isToday ? "2px solid hsl(var(--primary))" : color ? `1px solid ${color}` : "1px solid hsl(var(--border))",
                    opacity: day > now.getDate() && isCurrentMonth ? 0.4 : 1 }}>
                  {day}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-3 px-1">
            <span className="text-xs text-muted-foreground">Low</span>
            <div className="flex gap-1">{[1,2,3,4,5].map(s => <div key={s} className="w-4 h-4 rounded-sm" style={{ backgroundColor: SCORE_COLORS[s] + "cc" }} />)}</div>
            <span className="text-xs text-muted-foreground">High</span>
          </div>
        </div>
      )}
    </div>
  );
}
